import { scentMapping } from "@/helpers/scentMapping";
import {
  getEffectiveOnetimeSpotDiscountDollars,
  getEffectiveSubscriptionDiscountPercent,
} from "@/helpers/constants";
import {
  SUBSCRIPTION_CONTROL_PLAN_NAME,
  SUBSCRIPTION_PLAN_NAME_BY_VARIATION,
  SUBSCRIPTION_15_PLAN_DISCOUNT_PERCENT,
  getSubscriptionDiscountsByVariationFromLiquidAllocations,
  getSubscriptionDiscountVariation,
  getSubscriptionPlanNameForVariation,
} from "@/helpers/subscriptionDiscount";
import { formatPerPodDisplay } from "@/helpers/perPodPrice";

function getShortTitle(title: string): string {
  return title
    .split(",")[0]
    .replace(/laundry & household|pods|biobased|power/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function shortenTitle(title: string): string {
  return getShortTitle(title).replace(
    /Laundry Detergent/gi,
    "<br>Laundry\u00A0Detergent"
  );
}

function shortenModalTitle(title: string): string {
  return getShortTitle(title).replace(
    /Laundry Detergent/gi,
    "Laundry\u00A0Detergent"
  );
}

/** Formats "Count / 63 Loads" → "63 Count", "Standard / Count / 1000 Loads" → "1000 Count", "6 Refills" → "6 Refills", "Default Title" → "" */
function formatSize(variantTitle: string): string {
  if (!variantTitle || variantTitle === "Default Title") return "";
  if (variantTitle.includes("Refills")) {
    return variantTitle;
  }
  const parts = variantTitle.split("/");
  if (parts.length >= 2) {
    const unit = parts[parts.length - 2].trim();
    const rawValue = parts[parts.length - 1].trim();
    const numValue = parseFloat(rawValue);
    const value = !isNaN(numValue) ? numValue.toString() : rawValue;
    return `${value} ${unit}`;
  }
  return variantTitle;
}


/** Formats variant title for display in variant selector buttons — just the numeric value + "Count" */
function formatVariantButton(variantTitle: string): string {
  if (!variantTitle || variantTitle === "Default Title") return "";
  const parts = variantTitle.split("/");
  if (parts.length >= 2) {
    const rawValue = parts[parts.length - 1].trim();
    const numValue = parseFloat(rawValue);
    return !isNaN(numValue) ? `${numValue} Count` : rawValue;
  }
  return variantTitle;
}

function isVisibleVariant(variant: any): boolean {
  return variant.hidden !== true && variant.hidden !== "true";
}

function getVariantQuantityDiscount(variant: any): number {
  const rawValue = Number(variant?.quantity_discount ?? 0);
  return Number.isFinite(rawValue) && rawValue > 0 ? rawValue : 0;
}

function formatVariantSize(variant: any): string {
  if (variant?.override_title) return variant.override_title;
  return formatSize(variant?.title || "");
}

function formatSingleVariantSize(variant: any): string {
  if (variant?.override_title) return variant.override_title;
  return formatVariantButton(variant?.title || "");
}

function hasOrderableSubscription(activeProduct: any, variant: any): boolean {
  const productHidesSubscription =
    activeProduct?.hide_subscription === true ||
    activeProduct?.hide_subscription === "true";
  const variantHidesSubscription =
    variant?.hide_subscription_button === true ||
    variant?.hide_subscription_button === "true";
  return (
    !productHidesSubscription &&
    !variantHidesSubscription &&
    Array.isArray(variant?.selling_plan_allocations) &&
    variant.selling_plan_allocations.length > 0
  );
}

function getControlAllocation(allocations: any[] | undefined): any | undefined {
  if (!Array.isArray(allocations) || allocations.length === 0) return undefined;
  return (
    allocations.find(
      (a: any) => a.selling_plan_name === SUBSCRIPTION_CONTROL_PLAN_NAME
    ) || allocations[0]
  );
}

/** Same plan selection as PDP/store `defaultSellingPlan` for cart price + copy */
function resolveSubscriptionAllocation(variant: any): any | undefined {
  const allocations = variant?.selling_plan_allocations;
  const controlAlloc = getControlAllocation(allocations);
  if (!controlAlloc) return undefined;
  const controlPct =
    typeof controlAlloc.discount_percent === "number"
      ? controlAlloc.discount_percent
      : undefined;
  const planName = getSubscriptionPlanNameForVariation(controlPct);
  return (
    allocations.find((a: any) => a.selling_plan_name === planName) ||
    controlAlloc
  );
}

function productSubscriptionAbOptions(activeProduct: any | undefined) {
  const title = activeProduct?.title ?? "";
  const isBulk = title.toLowerCase().includes("bulk");
  const isBundle =
    Array.isArray(activeProduct?.tags) && activeProduct.tags.includes("bundle");
  return { isBulk, isBundle };
}

/** PLP card “Save X%” copy: 15% for treatment + -15 plan; else actual effective %. */
function subscriptionDiscountPercentForCardCopy(
  roundedEffective: number,
  variant: any
): number {
  const alloc = resolveSubscriptionAllocation(variant);
  if (
    getSubscriptionDiscountVariation() === 1 &&
    alloc?.selling_plan_name === SUBSCRIPTION_PLAN_NAME_BY_VARIATION[1]
  ) {
    return SUBSCRIPTION_15_PLAN_DISCOUNT_PERCENT;
  }
  return roundedEffective;
}

function getSubscriptionDiscountPercent(
  variant: any,
  activeProduct?: any
): number {
  if (!hasOrderableSubscription(activeProduct, variant)) return 0;
  const allocations = variant.selling_plan_allocations;
  const controlAlloc = getControlAllocation(allocations);
  if (!controlAlloc) return 0;

  const spPercent = controlAlloc.discount_percent;
  if (typeof spPercent === "number" && spPercent > 0) {
    const variationDiscounts =
      getSubscriptionDiscountsByVariationFromLiquidAllocations(allocations);
    const effective = getEffectiveSubscriptionDiscountPercent(
      spPercent,
      productSubscriptionAbOptions(activeProduct),
      variationDiscounts
    );
    return subscriptionDiscountPercentForCardCopy(
      Math.round(effective * 100) / 100,
      variant
    );
  }

  const basePrice = Number(variant?.price_raw ?? 0);
  const subscriptionPrice = Number(controlAlloc?.price_raw ?? 0);
  if (!basePrice || subscriptionPrice >= basePrice) return 0;

  const percent = ((basePrice - subscriptionPrice) / basePrice) * 100;
  const rounded = Math.round(percent * 100) / 100;
  const variationDiscounts =
    getSubscriptionDiscountsByVariationFromLiquidAllocations(allocations);
  const effective = getEffectiveSubscriptionDiscountPercent(
    rounded,
    productSubscriptionAbOptions(activeProduct),
    variationDiscounts
  );
  return subscriptionDiscountPercentForCardCopy(
    Math.round(effective * 100) / 100,
    variant
  );
}


function formatMoney(cents: number): string {
  if (!Number.isFinite(cents) || cents <= 0) return "FREE";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

function buildVariantUrl(productUrl: string): string {
  if (!productUrl) return "";

  try {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.delete("variant");
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return productUrl
      .replace(/([?&])variant=[^&#]*(&)?/, (_match, prefix, suffix) =>
        prefix === "?" && suffix ? "?" : suffix ? prefix : ""
      )
      .replace(/[?&]$/, "");
  }
}


function getPromoEntry(activeProduct: any) {
  const promoEntries = (window as any).promoDiscountEntries || [];
  const collections = Array.isArray(activeProduct?.collections)
    ? activeProduct.collections
    : [];

  return promoEntries
    .filter((entry: any) =>
      collections.some(
        (collection: any) =>
          collection === entry?.collection ||
          collection?.handle === entry?.collection ||
          collection?.id === entry?.collection
      )
    )
    .sort(
      (a: any, b: any) =>
        Number(b?.discountValue ?? 0) - Number(a?.discountValue ?? 0)
    )[0];
}

function getAtcPriceHtml(
  activeProduct: any,
  displayPrice: string,
  displayPriceRaw: number,
  applyPromoDiscount: boolean = true
): string {
  if (!applyPromoDiscount) {
    return `<span class="sr-only">Current price</span>${displayPrice}`;
  }
  const promoEntry = getPromoEntry(activeProduct);
  if (
    !promoEntry ||
    !Number.isFinite(displayPriceRaw) ||
    displayPriceRaw <= 0
  ) {
    return `<span class="sr-only">Current price</span>${displayPrice}`;
  }

  const discountType = String(promoEntry.discountType || "").toLowerCase();
  const discountValue = Number(promoEntry.discountValue || 0);
  let discountedCents = displayPriceRaw;

  if (discountType === "percentage") {
    discountedCents = Math.ceil(displayPriceRaw * (1 - discountValue / 100));
  } else if (discountType === "fixed") {
    discountedCents = Math.max(
      0,
      displayPriceRaw - Math.ceil(discountValue * 100)
    );
  } else {
    return `<span class="sr-only">Current price</span>${displayPrice}`;
  }

  return `<span class="sr-only">Current price</span><span class="mr-2 font-normal line-through group-hover:text-white">${displayPrice}</span><span class="group-hover:text-white">${formatMoney(
    discountedCents
  )}</span>`;
}

function getVariantToAdd(visibleVariants: any[], selectedVariant: any) {
  const selectedMultiplier = Number(
    selectedVariant?.tiered_discount_variant_multiplier
  );
  const baseVariant = visibleVariants.find(
    (variant: any) => Number(variant?.tiered_discount_variant_multiplier) === 1
  );

  if (Number.isFinite(selectedMultiplier)) {
    if (selectedMultiplier === 1) {
      return { variantToAdd: selectedVariant, quantity: 1 };
    }

    if (selectedMultiplier >= 2 && baseVariant) {
      return { variantToAdd: baseVariant, quantity: selectedMultiplier };
    }
  }

  return { variantToAdd: selectedVariant, quantity: 1 };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function getVariantSelectorAriaLabel(visibleVariants: any[]): string {
  const firstVariant = visibleVariants[0];
  if (!firstVariant || firstVariant.override_title) {
    return "Select product";
  }

  const parts = String(firstVariant.title || "").split("/");
  const unit = parts.length >= 2 ? parts[parts.length - 2].trim() : "";
  return unit ? `Select product ${unit}` : "Select product";
}

function renderVariantOptionsHtml(
  visibleVariants: any[],
  activeVariantIndex: number
): string {
  if (visibleVariants.length === 0) {
    return "";
  }

  if (visibleVariants.length === 1) {
    return `<p class="body js-single-variant-size"><span class="font-semibold">Size: </span><span class="js-single-variant-size-value">${escapeHtml(
      formatSingleVariantSize(visibleVariants[0])
    )}</span></p>`;
  }

  return `<div role="radiogroup" class="variant-option-buttons flex space-x-2" aria-label="${escapeHtml(
    getVariantSelectorAriaLabel(visibleVariants)
  )}">${visibleVariants
    .map((variant: any, index: number) => {
      const isSelected = index === activeVariantIndex;
      const quantityDiscount = getVariantQuantityDiscount(variant);

      return `<div data-id="variants-button" class="js-variant-button-wrapper flex-1 ${
        isSelected ? "bg-primary-50" : ""
      }">${
        quantityDiscount > 0
          ? `<p class="buy-more-save-more-label ${
              isSelected ? "bg-primary-800" : ""
            }">SAVE ${escapeHtml(String(quantityDiscount))}%</p>`
          : ""
      }<button role="radio" aria-checked="${
        isSelected ? "true" : "false"
      }" name="variant-button" data-variant-id="${escapeHtml(
        String(variant.id)
      )}" data-variant-index="${index}" class="js-variant-button MuiButton-root flex-1 rounded-[3px] border-0 bg-transparent px-1 font-normal normal-case tracking-normal text-gray-700 ring-1 ring-gray-700 aria-checked:font-bold aria-checked:text-primary aria-checked:ring-2 aria-checked:ring-primary w-full btn btn-outline " type="button"><span class="text-sm lg:text-base">${escapeHtml(
        formatSingleVariantSize(variant)
      )}</span></button></div>`;
    })
    .join("")}</div>`;
}

function buildRestockProductData(activeProduct: any) {
  return JSON.stringify({
    id: activeProduct.id,
    available: false,
    title: activeProduct.title,
    handle: activeProduct.handle,
    tags: activeProduct.tags || [],
    variants: (activeProduct.variants || []).map((variant: any) => ({
      id: variant.id,
      title: variant.title,
      available: false,
      inventory_management: "shopify",
      inventory_quantity: variant.inventory_quantity ?? 0,
    })),
  });
}

function renderProductBadge(container: HTMLElement, badge?: any) {
  const existingBadge = container.querySelector(".js-product-badge");
  if (existingBadge) {
    existingBadge.remove();
  }

  if (!badge || (!badge.text && !badge.image)) {
    return;
  }

  if (badge.text && badge.text.trim()) {
    const textBadge = document.createElement("div");
    textBadge.className =
      "js-product-badge absolute right-0 top-0 z-[2] inline-flex max-w-[120px] items-center justify-center bg-primary pointer-events-none";

    const text = document.createElement("p");
    text.className =
      "m-0 whitespace-nowrap px-2 py-1 text-center text-xs font-semibold leading-[130%] text-white sm:px-3 sm:py-2 sm:text-sm";
    text.textContent = badge.text;
    textBadge.appendChild(text);
    container.appendChild(textBadge);
    return;
  }

  if (badge.image && badge.image.trim()) {
    const imageBadge = document.createElement("div");
    const position =
      badge.position === "bottom-right" ? "bottom-right" : "top-right";
    const width = Number.isFinite(Number(badge.width))
      ? Number(badge.width)
      : 30;
    imageBadge.className =
      "js-product-badge pointer-events-none absolute right-2 z-[2] flex items-center justify-end";
    imageBadge.setAttribute(
      "style",
      `${
        position === "top-right" ? "top: 8px;" : "bottom: 8px;"
      } width: calc(${width}% - 16px);`
    );

    const image = document.createElement("img");
    image.src = badge.image;
    image.alt = "";
    image.loading = "lazy";
    image.className = `block h-auto max-w-full object-contain ${
      position === "top-right" ? "object-top" : "object-bottom"
    }`;
    imageBadge.appendChild(image);
    container.appendChild(imageBadge);
  }
}

export class NativeProductCard {
  container: HTMLElement;
  scope: HTMLElement;
  activeProductId: number = 0;
  products: any[] = [];
  variants: any[] = [];
  activeVariantIndex: number = 0;
  purchaseType: "subscription" | "onetime" = "subscription";
  lastFocusedElement: HTMLElement | null = null;

  get visibleVariants() {
    return this.variants.filter((variant: any) => isVisibleVariant(variant));
  }

  constructor(container: HTMLElement) {
    this.container = container;
    this.scope =
      (container.closest(".scent-consolidation-slider") as HTMLElement) ||
      container;
    const jsonScript = this.container.querySelector(
      ".product-card-json"
    ) as HTMLScriptElement;
    if (!jsonScript) return;

    try {
      const data = JSON.parse(jsonScript.textContent || "{}");
      this.activeProductId = data.id || 0;
      this.variants = data.variants || [];

      // Store all products available in this group.
      const seenProductIds = new Set<number | string>([data.id]);
      this.products = [data];
      if (data.collection_products && data.collection_products.length > 0) {
        data.collection_products.forEach((p: any) => {
          if (p.id !== data.id && !seenProductIds.has(p.id)) {
            seenProductIds.add(p.id);
            this.products.push(p);
          }
        });
      }
    } catch (e) {
      console.error("Failed to parse product card JSON", e);
      return;
    }

    // Find initial state based on DOM
    const initialCheckbox = this.container.querySelector(
      ".js-subscribe-checkbox"
    ) as HTMLInputElement | null;
    if (initialCheckbox) {
      this.purchaseType = initialCheckbox.checked ? "subscription" : "onetime";
    }

    const mobileQuickAddModal = this.container.querySelector(
      ".js-mobile-quick-add-modal"
    );
    if (
      mobileQuickAddModal &&
      mobileQuickAddModal.parentElement !== this.scope
    ) {
      this.scope.appendChild(mobileQuickAddModal);
    }

    this.bindEvents();
    this.render(); // Ensure DOM matches state on init
  }

  bindEvents() {
    const quickAddOpenBtns = this.scope.querySelectorAll(
      ".js-mobile-quick-add-open"
    );
    quickAddOpenBtns.forEach((btn: Element) => {
      btn.addEventListener("click", (e: Event) => {
        e.preventDefault();
        this.openMobileQuickAddModal();
      });
    });

    const quickAddCloseBtns = this.scope.querySelectorAll(
      ".js-mobile-quick-add-close"
    );
    quickAddCloseBtns.forEach((btn: Element) => {
      btn.addEventListener("click", (e: Event) => {
        e.preventDefault();
        this.closeMobileQuickAddModal();
      });
    });

    const quickAddModal = this.scope.querySelector(
      ".js-mobile-quick-add-modal"
    );
    if (quickAddModal) {
      quickAddModal.addEventListener("click", (e: Event) => {
        if (e.target === quickAddModal) {
          this.closeMobileQuickAddModal();
        }
      });
      quickAddModal.addEventListener(
        "keydown",
        this.handleModalKeydown as EventListener
      );
    }

    this.scope.addEventListener(
      "cart:add:success",
      this.handleCartAddSuccess as EventListener
    );
  }

  bindInteractiveControls() {
    const variantBtns = this.scope.querySelectorAll(".js-variant-button");
    variantBtns.forEach((btn: Element) => {
      (btn as HTMLElement).onclick = (e: MouseEvent) => {
        e.preventDefault();
        const index = Number((btn as HTMLElement).dataset.variantIndex ?? "0");
        this.activeVariantIndex = Number.isFinite(index) ? index : 0;
        this.render();
      };
    });

    const subCheckboxes = this.scope.querySelectorAll(".js-subscribe-checkbox");
    subCheckboxes.forEach((cb: Element) => {
      (cb as HTMLInputElement).onchange = () => {
        this.purchaseType = (cb as HTMLInputElement).checked
          ? "subscription"
          : "onetime";
        this.render();
      };
    });

    const scentOptions = this.scope.querySelectorAll(".js-scent-option");
    scentOptions.forEach((btn: Element) => {
      (btn as HTMLElement).onclick = (e: MouseEvent) => {
        e.preventDefault();
        const newProductId = parseInt(
          (btn as HTMLElement).dataset.productId || "0",
          10
        );
        if (newProductId && newProductId !== this.activeProductId) {
          this.switchProduct(newProductId);
        }
      };

      (btn as HTMLElement).onmouseenter = () => {
        const hoveredId = parseInt(
          (btn as HTMLElement).dataset.productId || "0",
          10
        );
        const hoveredProduct = this.products.find(
          (p: any) => p.id === hoveredId
        );
        if (hoveredProduct) {
          this.updatePreviewState(hoveredProduct);
        }
      };

      (btn as HTMLElement).onmouseleave = () => {
        const activeProduct = this.products.find(
          (p: any) => p.id === this.activeProductId
        );
        if (activeProduct) {
          this.updatePreviewState(activeProduct);
        }
      };
    });
  }

  handleCartAddSuccess = () => {
    const modal = this.scope.querySelector(
      ".js-mobile-quick-add-modal"
    ) as HTMLElement | null;
    if (modal && !modal.classList.contains("hidden")) {
      this.closeMobileQuickAddModal();
    }
  };

  handleModalKeydown = (event: KeyboardEvent) => {
    const modal = this.scope.querySelector(
      ".js-mobile-quick-add-modal"
    ) as HTMLElement | null;
    if (!modal || modal.classList.contains("hidden")) return;

    if (event.key === "Escape") {
      event.preventDefault();
      this.closeMobileQuickAddModal();
      return;
    }

    if (event.key !== "Tab") return;

    const focusableElements = Array.from(
      modal.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.offsetParent !== null);

    if (focusableElements.length === 0) {
      event.preventDefault();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement as HTMLElement | null;

    if (event.shiftKey) {
      if (
        !activeElement ||
        activeElement === firstElement ||
        !modal.contains(activeElement)
      ) {
        event.preventDefault();
        lastElement.focus();
      }
      return;
    }

    if (
      !activeElement ||
      activeElement === lastElement ||
      !modal.contains(activeElement)
    ) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  openMobileQuickAddModal() {
    const modal = this.scope.querySelector(
      ".js-mobile-quick-add-modal"
    ) as HTMLElement | null;
    if (!modal) return;

    this.lastFocusedElement =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    modal.classList.remove("hidden");
    document.documentElement.classList.add("overflow-hidden");
    document.body.classList.add("overflow-hidden");

    const closeButton = modal.querySelector(
      ".js-mobile-quick-add-close"
    ) as HTMLElement | null;
    closeButton?.focus();
  }

  closeMobileQuickAddModal() {
    const modal = this.scope.querySelector(
      ".js-mobile-quick-add-modal"
    ) as HTMLElement | null;
    if (!modal) return;

    modal.classList.add("hidden");
    document.documentElement.classList.remove("overflow-hidden");
    document.body.classList.remove("overflow-hidden");

    if (this.lastFocusedElement && document.contains(this.lastFocusedElement)) {
      this.lastFocusedElement.focus();
    }
    this.lastFocusedElement = null;
  }

  updatePreviewImage(product: any) {
    if (!product?.featured_image) return;

    const imageNodes = this.scope.querySelectorAll(
      ".js-product-image"
    ) as NodeListOf<HTMLImageElement>;
    imageNodes.forEach((img: HTMLImageElement) => {
      img.src = product.featured_image;
      img.alt = product.title || "";
    });
  }

  updatePreviewRatings(product: any) {
    if (!product?.id) return;

    const showRatings = !product.tags?.includes("hide-reviews");
    const ratingsWrappers = this.scope.querySelectorAll(
      '[data-id="product-card-ratings"]'
    );
    ratingsWrappers.forEach((ratingsWrapper: Element) => {
      ratingsWrapper.classList.toggle("hidden", !showRatings);
    });

    const ratingsNodes = this.scope.querySelectorAll("[data-bv-product-id]");
    ratingsNodes.forEach((ratingsNode: Element) => {
      ratingsNode.setAttribute("data-bv-product-id", product.id.toString());
    });

    if (showRatings && ratingsNodes.length > 0 && (window as any).$BV) {
      (window as any).$BV.ui("inline_rating", "submit", {
        productIds: [product.id.toString()],
      });
    }
  }

  updatePreviewState(product: any) {
    if (!product) return;

    this.updatePreviewImage(product);
    this.updatePreviewRatings(product);
  }

  switchProduct(newProductId: number) {
    const newProduct = this.products.find((p: any) => p.id === newProductId);
    if (!newProduct) return;

    this.activeProductId = newProductId;
    this.variants = newProduct.variants || [];
    this.activeVariantIndex = 0; // Reset to first variant
    this.render();
  }

  render() {
    const activeProduct = this.products.find(
      (p: any) => p.id === this.activeProductId
    );
    const visibleVariants = this.visibleVariants;
    if (!activeProduct || visibleVariants.length === 0) return;
    if (this.activeVariantIndex >= visibleVariants.length) {
      this.activeVariantIndex = 0;
    }
    const variant = visibleVariants[this.activeVariantIndex];

    const { variantToAdd, quantity } = getVariantToAdd(
      visibleVariants,
      variant
    );
    const hasTieredMultiplierFeature = visibleVariants.some((v: any) => {
      const raw =
        v?.tiered_discount_variant_multiplier ??
        v?.metafields?.tiered_discount_variant_multiplier;
      const multiplier = Number(raw);
      return Number.isFinite(multiplier) && multiplier > 1;
    });
    const canSubscribe = hasOrderableSubscription(activeProduct, variant);
    if (!canSubscribe && this.purchaseType !== "onetime") {
      this.purchaseType = "onetime";
    }

    // Determine selling plan and prices
    const selectedQuantityDiscount = hasTieredMultiplierFeature
      ? getVariantQuantityDiscount(variant)
      : 0;
    let unitPriceRaw = Number(
      variantToAdd?.price_raw ?? variant?.price_raw ?? 0
    );
    if (selectedQuantityDiscount > 0) {
      unitPriceRaw = Math.ceil(
        unitPriceRaw * (1 - selectedQuantityDiscount / 100)
      );
    }
    if (this.purchaseType === "onetime") {
      const spotDollars = getEffectiveOnetimeSpotDiscountDollars(
        variant,
        activeProduct,
        productSubscriptionAbOptions(activeProduct)
      );
      if (spotDollars > 0) {
        const offsetCents = Math.round(spotDollars * 100);
        unitPriceRaw = Math.max(0, unitPriceRaw - offsetCents);
      }
    }
    let displayPriceRaw = unitPriceRaw * quantity;
    let displayPrice = formatMoney(displayPriceRaw);
    const unitCompareAtPriceRaw = Number(
      variantToAdd?.compare_at_price_raw ?? variant?.compare_at_price_raw ?? 0
    );
    const compareAtPriceRaw =
      unitCompareAtPriceRaw > 0 ? unitCompareAtPriceRaw * quantity : 0;
    const compareAtPrice =
      compareAtPriceRaw > 0 ? formatMoney(compareAtPriceRaw) : "";
    let sellingPlanId = "";
    const onetimePriceRaw = displayPriceRaw;
    const onetimePrice = displayPrice;

    if (this.purchaseType === "subscription" && canSubscribe) {
      const allocation = resolveSubscriptionAllocation(variantToAdd);
      if (allocation) {
        unitPriceRaw = Number(allocation?.price_raw ?? unitPriceRaw);
        if (selectedQuantityDiscount > 0) {
          unitPriceRaw = Math.ceil(
            unitPriceRaw * (1 - selectedQuantityDiscount / 100)
          );
        }
        displayPriceRaw = unitPriceRaw * quantity;
        displayPrice = formatMoney(displayPriceRaw);
      }

      if (allocation) {
        sellingPlanId = allocation.selling_plan_id;
      }
    }

    // Update Product Title
    const titleNodes = this.scope.querySelectorAll(".js-product-title");
    titleNodes.forEach((node: Element) => {
      if (node.closest(".js-mobile-quick-add-modal")) {
        node.innerHTML = shortenModalTitle(activeProduct.title);
        return;
      }

      node.innerHTML = shortenTitle(activeProduct.title);
    });

    const departmentNodes = this.scope.querySelectorAll(
      ".js-product-department"
    );
    departmentNodes.forEach((node: Element) => {
      const department = activeProduct.type || "";
      node.textContent = department;
      node.classList.toggle("hidden", !department);
    });

    // Update Bazaarvoice Ratings
    const showRatings = !activeProduct.tags?.includes("hide-reviews");
    const ratingsWrappers = this.scope.querySelectorAll(
      '[data-id="product-card-ratings"]'
    );
    ratingsWrappers.forEach((ratingsWrapper: Element) => {
      ratingsWrapper.classList.toggle("hidden", !showRatings);
    });
    const ratingsNodes = this.scope.querySelectorAll("[data-bv-product-id]");
    ratingsNodes.forEach((ratingsNode: Element) => {
      ratingsNode.setAttribute(
        "data-bv-product-id",
        activeProduct.id.toString()
      );
    });
    if (showRatings && ratingsNodes.length > 0 && (window as any).$BV) {
      (window as any).$BV.ui("inline_rating", "submit", {
        productIds: [activeProduct.id.toString()],
      });
    }

    const variantOptionGroups = this.scope.querySelectorAll(
      ".plp-box-sizes_variant-options"
    );
    variantOptionGroups.forEach((group: Element) => {
      group.innerHTML = renderVariantOptionsHtml(
        visibleVariants,
        this.activeVariantIndex
      );
    });

    const imageNodes = this.scope.querySelectorAll(
      ".js-product-image"
    ) as NodeListOf<HTMLImageElement>;
    imageNodes.forEach((img: HTMLImageElement) => {
      if (activeProduct.featured_image) {
        img.src = activeProduct.featured_image;
        img.alt = activeProduct.title;
      }
    });

    const primaryImageContainer = this.scope.querySelector(
      ".js-product-image-container"
    ) as HTMLElement | null;
    if (primaryImageContainer) {
      renderProductBadge(primaryImageContainer, activeProduct.badge);
    }

    const linkNodes = this.scope.querySelectorAll(
      'a[href*="/products/"]'
    ) as NodeListOf<HTMLAnchorElement>;
    linkNodes.forEach((link: HTMLAnchorElement) => {
      if (link.classList.contains("js-learn-more-link")) {
        link.href = `/products/${activeProduct.handle}?variant=${variant.id}`;
      } else {
        link.href = buildVariantUrl(activeProduct.url);
      }
    });

    // 1. Update Variant Button Classes
    const variantBtns = this.scope.querySelectorAll(".js-variant-button");
    variantBtns.forEach((btn: Element) => {
      const index = Number((btn as HTMLElement).dataset.variantIndex ?? "0");
      const isSelected = index === this.activeVariantIndex;
      btn.setAttribute("aria-checked", isSelected ? "true" : "false");
    });

    // 2. Update Subscription Checkbox UI
    const isSubscribed = this.purchaseType === "subscription";
    const subscriptionDiscount = getSubscriptionDiscountPercent(
      variant,
      activeProduct
    );

    const subscriptionSelectors = this.scope.querySelectorAll(
      ".js-subscription-selector"
    );
    subscriptionSelectors.forEach((subscriptionSelector: Element) => {
      subscriptionSelector.classList.toggle("hidden", !canSubscribe);
    });

    const subCheckboxes = this.scope.querySelectorAll(".js-subscribe-checkbox");
    subCheckboxes.forEach((cb: Element) => {
      (cb as HTMLInputElement).checked = isSubscribed;
    });

    const subLabelTexts = this.scope.querySelectorAll(".js-subscribe-label");
    subLabelTexts.forEach((span: Element) => {
      (span as HTMLElement).textContent =
        subscriptionDiscount > 0
          ? `Subscribe & Save ${subscriptionDiscount}%`
          : "Subscribe";
    });

    const isBulk = (activeProduct?.title ?? "")
      .toLowerCase()
      .includes("bulk");
    const perPodText = formatPerPodDisplay({
      displayPriceCents: displayPriceRaw,
      packageQuantity: Number(variant?.package_quantity ?? 0),
      productType: activeProduct?.type,
      isBulk,
    });
    const perPodNodes = this.scope.querySelectorAll(".js-product-per-pod");
    perPodNodes.forEach((node: Element) => {
      (node as HTMLElement).textContent = perPodText;
    });

    // 3. Update Prices
    const priceNodes = this.scope.querySelectorAll(".js-product-price");
    priceNodes.forEach((node: Element) => {
      node.innerHTML = `<span class="sr-only">Current price</span>${displayPrice}`;
    });

    const compareAtPriceNodes = this.scope.querySelectorAll(
      ".js-product-compare-at-price"
    );
    const isBundleForCompare = activeProduct?.type === "Bundle";
    compareAtPriceNodes.forEach((node: Element) => {
      if (isBundleForCompare && compareAtPriceRaw > 0) {
        node.classList.remove("hidden");
        node.innerHTML = `<span class="sr-only">Regular price</span>${compareAtPrice}`;
      } else {
        node.classList.add("hidden");
        node.innerHTML = "";
      }
    });

    const addCartPriceNodes = this.scope.querySelectorAll(
      ".js-add-to-cart-price"
    );
    const hasQuantityDiscount = getVariantQuantityDiscount(variant) > 0;
    const defaultVariantMultiplier = Number(
      variant?.default_variant_multiplier ??
        variant?.metafields?.default_variant_multiplier ??
        1
    );
    const hasDefaultMultiplier =
      Number.isFinite(defaultVariantMultiplier) && defaultVariantMultiplier > 1;
    addCartPriceNodes.forEach((node: Element) => {
      node.innerHTML = getAtcPriceHtml(
        activeProduct,
        displayPrice,
        displayPriceRaw,
        !hasQuantityDiscount && !hasDefaultMultiplier
      );
    });

    const isBundleType = activeProduct?.type === "Bundle";
    const atcCompareNodes = this.scope.querySelectorAll(".js-atc-compare-price");
    atcCompareNodes.forEach((node: Element) => {
      let showCompare = false;
      let compareText = "";

      if (!isBundleType) {
        if (compareAtPriceRaw > 0 && compareAtPriceRaw > displayPriceRaw) {
          showCompare = true;
          compareText = compareAtPrice;
        } else if (
          this.purchaseType === "subscription" &&
          canSubscribe &&
          onetimePrice !== displayPrice
        ) {
          showCompare = true;
          compareText = onetimePrice;
        }
      } else {
        if (compareAtPriceRaw > 0) {
          showCompare = true;
          compareText = compareAtPrice;
        }
      }

      if (showCompare) {
        node.classList.remove("hidden");
        node.textContent = compareText;
      } else {
        node.classList.add("hidden");
        node.textContent = "";
      }
    });

    // 4. Update Size Text
    const sizeNodes = this.scope.querySelectorAll(".js-product-size");
    sizeNodes.forEach((node: Element) => {
      node.textContent = formatVariantSize(variant);
    });

    // 4b. Update Variant Button Labels
    const variantBtnSpans = this.scope.querySelectorAll(
      ".js-variant-button span"
    );
    variantBtnSpans.forEach((span: Element) => {
      const parentButton = span.closest(
        ".js-variant-button"
      ) as HTMLElement | null;
      const index = Number(parentButton?.dataset.variantIndex ?? "0");
      const selectedVariant = visibleVariants[index];
      if (selectedVariant) {
        span.textContent = formatSingleVariantSize(selectedVariant);
      }
    });

    const singleVariantSizeValues = this.scope.querySelectorAll(
      ".js-single-variant-size-value"
    );
    singleVariantSizeValues.forEach((singleVariantSizeValue: Element) => {
      singleVariantSizeValue.textContent = formatSingleVariantSize(variant);
    });

    // 6. Update form inputs
    const variantInputs = this.scope.querySelectorAll(
      ".js-variant-id"
    ) as NodeListOf<HTMLInputElement>;
    variantInputs.forEach((input: HTMLInputElement) => {
      input.value = variantToAdd.id.toString();
    });

    const quantityInputs = this.scope.querySelectorAll(
      ".js-quantity"
    ) as NodeListOf<HTMLInputElement>;
    quantityInputs.forEach((input: HTMLInputElement) => {
      input.value = quantity.toString();
    });

    const sellingPlanInputs = this.scope.querySelectorAll(
      ".js-selling-plan-id"
    ) as NodeListOf<HTMLInputElement>;
    sellingPlanInputs.forEach((input: HTMLInputElement) => {
      input.value = sellingPlanId ? sellingPlanId.toString() : "";
      if (!sellingPlanId) {
        input.removeAttribute("name");
      } else {
        input.setAttribute("name", "selling_plan");
      }
    });

    const sourceModuleInputs = this.scope.querySelectorAll(
      ".js-source-module"
    ) as NodeListOf<HTMLInputElement>;
    const isBundle =
      activeProduct?.tags?.includes("bundle") &&
      !String(activeProduct?.handle || "").includes("starter-kit");
    sourceModuleInputs.forEach((input: HTMLInputElement) => {
      input.value = isBundle ? "bundle_plp_quick_add" : "plp_quick_add";
    });

    const overrideTitleInputs = this.scope.querySelectorAll(
      ".js-override-title"
    ) as NodeListOf<HTMLInputElement>;
    const overrideTitle = variantToAdd?.override_title || "";
    overrideTitleInputs.forEach((input: HTMLInputElement) => {
      if (overrideTitle) {
        input.setAttribute("name", "properties[_override_title]");
        input.value = overrideTitle;
      } else {
        input.removeAttribute("name");
        input.value = "";
      }
    });

    // 7. Update Add to cart button state
    const addToCartBtns = this.scope.querySelectorAll(
      ".js-add-to-cart"
    ) as NodeListOf<HTMLButtonElement>;
    const addToCartTexts = this.scope.querySelectorAll(".js-add-to-cart-text");
    addToCartBtns.forEach((btn: HTMLButtonElement) => {
      if (variant.available) {
        btn.removeAttribute("disabled");
      } else {
        btn.setAttribute("disabled", "disabled");
      }
    });

    addToCartTexts.forEach((text: Element) => {
      const isMobileBtn = !!text.closest(".js-product-form-mobile");
      text.textContent = variant.available
        ? isMobileBtn
          ? "Quick Add +"
          : "Add to Cart"
        : "Out of Stock";
    });

    const notifyWrappers = this.scope.querySelectorAll(
      ".js-notify-restocked-wrapper"
    );
    notifyWrappers.forEach((wrapper: Element) => {
      wrapper.classList.toggle("hidden", variant.available);
    });

    const notifyBtns = this.scope.querySelectorAll(".js-notify-restocked-btn");
    notifyBtns.forEach((btn: Element) => {
      btn.setAttribute("data-variant-id", variant.id.toString());
      btn.setAttribute("data-product-handle", activeProduct.handle || "");
      btn.setAttribute(
        "data-product-data",
        buildRestockProductData(activeProduct)
      );
    });

    // 8. Update scent pill buttons (ring + name text)
    const scentOptions = this.scope.querySelectorAll(".js-scent-option");
    scentOptions.forEach((btn: Element) => {
      const btnId = parseInt((btn as HTMLElement).dataset.productId || "0", 10);
      const isSelected = btnId === this.activeProductId;

      if (isSelected) {
        btn.classList.add("ring-2");
        btn.classList.remove("ring-0");
      } else {
        btn.classList.add("ring-0");
        btn.classList.remove("ring-2");
      }

      const scentProduct = this.products.find((p: any) => p.id === btnId);
      if (scentProduct) {
        (btn as HTMLElement).style.backgroundColor =
          scentProduct.scent_color || "#f4f4f4";
        (btn as HTMLElement).dataset.scentName = scentProduct.scent_name || "";

        const nameSpan = btn.querySelector("span:not(.sr-only)") as HTMLElement;
        if (nameSpan) {
          nameSpan.textContent = scentProduct.scent_name || "";
        }
      }
    });

    this.bindInteractiveControls();
  }
}

export function initNativeProductCards() {
  document
    .querySelectorAll('[data-id="product-card"]')
    .forEach((container: Element) => {
      if (!(container as any)._nativeCardInit) {
        new NativeProductCard(container as HTMLElement);
        (container as any)._nativeCardInit = true;
      }
    });
}
